/* ---------------------------------------------------------------------------
 *  src/index.js
 * ---------------------------------------------------------------------------
 *  – Hydrate both user & creator token stores
 *  – If *either* token exists (and we’re not already on a deep-link), move
 *    the browser history to the right start route *before* React mounts.
 *    This guarantees the correct page flashes first, even on hard refreshes.
 * ------------------------------------------------------------------------ */

import React                 from 'react';
import ReactDOM              from 'react-dom/client';
import App                   from './App';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Capacitor }         from '@capacitor/core';

import {
  initAuthStorage,
  getAuthToken           as getUserToken,
} from './API/token-util';
import {
  initCreatorAuthStorage,
  getAuthToken           as getCreatorToken,
} from './API/creatorTokenUtil';
import routes              from './Config/routes';

import './index.css';

// ────────────────────────────────────────────────────────────
// Google OAuth client-ID (runtime platform switch)
// ────────────────────────────────────────────────────────────
const IOS_ID = '489265257959-374fsc04dffos29tto4jsqcd2sch5f9o.apps.googleusercontent.com';
const WEB_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';
const googleClientId =
  Capacitor.getPlatform() === 'ios' ? IOS_ID : WEB_ID;

// ────────────────────────────────────────────────────────────
// BOOTSTRAP
// ────────────────────────────────────────────────────────────
(async () => {
  // 1) Hydrate both token stores (Keychain / cookies → memory)
  await Promise.all([
    initAuthStorage(),          // user role
    initCreatorAuthStorage(),   // creator role
  ]);

  // 2) Decide where we *should* start
  const creatorJWT = getCreatorToken();
  const userJWT    = getUserToken();

  let startPath = null;
  if (creatorJWT)       startPath = routes.creatorConnectStart; // e.g. "/creator"
  else if (userJWT)     startPath = routes.dashboard;           // e.g. "/dashboard"

  // Avoid clobbering deep-links or explicit URLs
  const isRoot      = window.location.pathname === '/' || window.location.pathname === routes.base;
  const shouldBump  = startPath && isRoot;

  if (shouldBump) {
    // Replace the current history entry so back-button still works nicely
    window.history.replaceState({}, '', startPath);
  }

  // 3) Mount React
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <GoogleOAuthProvider clientId={googleClientId}>
        <App />
      </GoogleOAuthProvider>
    </React.StrictMode>
  );
})();
